import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import type { OrderPurchaseListItem } from "@/lib/types";

interface Props {
  item: OrderPurchaseListItem;
  mode: "partial" | "none";
  onCancel: () => void;
  onSubmit: (input: { purchasedQuantity: number; reason: string }) => Promise<void>;
}

export function PurchaseDecisionModal({ item, mode, onCancel, onSubmit }: Props) {
  const [quantity, setQuantity] = useState(String(item.purchasedQuantity ?? item.purchaseQuantity));
  const [reason, setReason] = useState(item.reason ?? "");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error("Motivo obrigatório");
      return;
    }
    const q = Number(quantity);
    if (mode === "partial" && (!Number.isFinite(q) || q <= 0)) {
      toast.error("Quantidade inválida");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ purchasedQuantity: q, reason: trimmed });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "partial" ? "Informar quantidade comprada" : "Item não comprado"}
          </DialogTitle>
          <DialogDescription>
            {item.productName} · previsto <strong>{item.purchaseQuantity}</strong> {item.unit}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {mode === "partial" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Quantidade comprada ({item.unit})
              </label>
              <Input
                type="number"
                min="0"
                step="0.001"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                autoFocus
              />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Motivo <span className="text-destructive">*</span>
            </label>
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={mode === "partial" ? "Por que não foi comprado o total?" : "Por que não foi comprado?"}
              className="min-h-[80px]"
              autoFocus={mode === "none"}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="hero" size="sm" onClick={() => void submit()} disabled={submitting}>
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
